/**
 * The brand, read from design/brand.json — the one file to edit to rebrand a
 * project (DESIGN.md). `pnpm brand` regenerates head tags, manifest and the
 * accent tokens from the same file.
 */
import brandJson from '#brand'
import { isSandbox } from './environment.ts'

export interface Brand {
  name: string
  shortName: string
  tagline: string
  accent: string
  /**
   * Path under public/ to a square logo (e.g. "/brand/mark.png"), or one per
   * surface when the brand's mark changes colour with what it sits on (a green
   * tile on light pages, a white tile on a green sidebar). Optional — the
   * sidebar and emails fall back to a generic mark when unset.
   */
  mark?: string | { surface: string; sidebar: string }
  /** A short line shown above the name in the sidebar (e.g. a company name). Optional. */
  eyebrow?: string
  /**
   * An outlined wordmark (the eyebrow and name drawn as one image, so they keep
   * their exact proportions everywhere). Paths under public/. When set, the
   * lockup draws it instead of typesetting the eyebrow and name.
   * `surface` for light pages, `sidebar` for the sidebar's colour. `email` is a
   * PNG (mail apps block SVG) shown 40px tall; `width` is its width at that
   * height, because Outlook sizes images only by their stated dimensions.
   */
  wordmark?: {
    surface: string
    sidebar: string
    email?: { src: string; width: number }
  }
  /**
   * Font family names (not full CSS stacks) for the brand's display and UI
   * type. Optional — read only by the email layout, which cannot use the
   * app's --font-display / --font-sans tokens (email clients ignore CSS
   * variables and web fonts are unreliable there), so it puts these first in
   * a literal fallback stack instead. The app itself gets its display font
   * from --font-display in brand.css, not from here.
   */
  fonts?: { display?: string; ui?: string }
  /**
   * Literal email colours, matching the app's canvas/surface/text/muted/
   * faint/divider tokens for a brand whose palette isn't neutral grey.
   * Optional — email clients ignore CSS variables, so `layout()` in
   * src/adapters/email/index.ts falls back to its own generic EMAIL_NEUTRALS
   * when this is unset. Kept here, deliberately, as the one duplicate of the
   * app's palette that email needs — keep it in step with brand.css by hand.
   */
  email?: {
    canvas?: string
    surface?: string
    text?: string
    muted?: string
    faint?: string
    divider?: string
  }
  user: { name: string; email: string; initials: string; role: string }
  base: { name: string; version: string }
  /**
   * Off by default (D-009). `true` brings back the Admin-only Test mode
   * switch and the server honouring `x-test-mode` — golden records and
   * unfinished features, never real data (docs/GOLDEN-RECORDS.md). Read
   * through the one place that means it, `src/lib/flags.ts` — never here
   * directly, so the header and the server can't disagree.
   */
  testMode?: boolean
  /**
   * On by default. `false` hides webhooks from people — the Settings link and
   * the Admin's data-health banner — while the code, its address and its
   * security tests stay (Majik has none yet, docs/DECISIONS.md D-020). Read
   * through `src/lib/flags.ts`.
   */
  webhooks?: boolean
}

export const brand = brandJson as Brand

/** Document title format from the kit: "{Page} · {App}". */
export function pageTitle(page: string, unread = 0): string {
  // A sandbox tab says so, so it is never mistaken for the live app when both
  // are open side by side (docs/ENVIRONMENTS.md).
  const environment = isSandbox ? '[Sandbox] ' : ''
  const prefix = unread > 0 ? `(${unread}) ` : ''
  return `${environment}${prefix}${page} · ${brand.name}`
}

/** The mark to draw on a surface — the single mark, or that surface's colourway. */
export function markFor(tone: 'surface' | 'sidebar'): string | undefined {
  const mark = brand.mark
  return typeof mark === 'string' ? mark : mark?.[tone]
}
